import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, OnDestroy, PLATFORM_ID, DOCUMENT } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemePreference = 'light' | 'dark' | 'system';
type ViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => { finished?: Promise<void> };
};

@Injectable({
  providedIn: 'root'
})
export class ThemeService implements OnDestroy {
  private readonly storageKey = 'tracit.theme';
  private readonly transitionClass = 'theme-transitioning';
  private transitionTimeout: ReturnType<typeof setTimeout> | null = null;
  private mediaQuery: MediaQueryList | null = null;
  private readonly isBrowser: boolean;
  private readonly preferenceSubject = new BehaviorSubject<ThemePreference>('system');
  private readonly isDarkSubject = new BehaviorSubject<boolean>(false);
  private readonly listener = () => this.applyCurrentPreference();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (!this.isBrowser) return;

    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.preferenceSubject.next(this.readPreference());
    this.applyCurrentPreference();
    this.mediaQuery.addEventListener('change', this.listener);
  }

  getPreference$(): Observable<ThemePreference> {
    return this.preferenceSubject.asObservable();
  }

  isDarkMode$(): Observable<boolean> {
    return this.isDarkSubject.asObservable();
  }

  setPreference(preference: ThemePreference): void {
    if (!this.isBrowser) return;

    this.preferenceSubject.next(preference);
    this.writePreference(preference);
    this.applyCurrentPreference();
  }

  toggleDarkMode(): void {
    this.setPreference(this.isDarkSubject.value ? 'light' : 'dark');
  }

  private applyCurrentPreference(): void {
    const preference = this.preferenceSubject.value;
    const dark = preference === 'system'
      ? !!this.mediaQuery?.matches
      : preference === 'dark';

    this.apply(dark);
  }

  private apply(dark: boolean): void {
    const root = this.document.documentElement;
    const currentTheme = root.getAttribute('data-bs-theme');
    const nextTheme = dark ? 'dark' : 'light';
    const shouldTransition = this.isBrowser
      && currentTheme !== null
      && currentTheme !== nextTheme
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (shouldTransition) {
      const transitionDocument = this.document as ViewTransitionDocument;

      if (transitionDocument.startViewTransition && this.document.visibilityState === 'visible') {
        this.startThemeTransition(460);
        try {
          const transition = transitionDocument.startViewTransition(() => {
            root.setAttribute('data-bs-theme', nextTheme);
            this.isDarkSubject.next(dark);
          });

          void transition.finished?.then(
            () => this.stopThemeTransition(),
            () => this.stopThemeTransition()
          );
          return;
        } catch {
          this.stopThemeTransition();
        }
      }

      this.startThemeTransition();
    }

    root.setAttribute('data-bs-theme', nextTheme);
    this.isDarkSubject.next(dark);
  }

  private startThemeTransition(duration = 380): void {
    const root = this.document.documentElement;

    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
    }

    root.classList.add(this.transitionClass);
    this.transitionTimeout = setTimeout(() => {
      this.stopThemeTransition();
    }, duration);
  }

  private stopThemeTransition(): void {
    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
      this.transitionTimeout = null;
    }

    this.document.documentElement.classList.remove(this.transitionClass);
  }

  private readPreference(): ThemePreference {
    const preference = this.getLocalStorageValue();

    return preference === 'light' || preference === 'dark' || preference === 'system'
      ? preference
      : 'system';
  }

  private writePreference(preference: ThemePreference): void {
    try {
      localStorage.setItem(this.storageKey, preference);
    } catch {
      // Ignore storage failures so the in-session toggle still works.
    }
  }

  private getLocalStorageValue(): string | null {
    try {
      return localStorage.getItem(this.storageKey);
    } catch {
      return null;
    }
  }

  ngOnDestroy(): void {
    this.stopThemeTransition();

    this.mediaQuery?.removeEventListener('change', this.listener);
  }
}
